import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const Container = styled.div`
  padding: 40px;
  min-height: 100vh;
  background-color: #f3f4f6;
  animation: ${fadeIn} 0.5s ease-out;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

export const Header = styled.div`
  margin-bottom: 30px;

  h1 {
    font-size: 28px;
    font-weight: 800;
    color: #111827;
    margin: 0 0 5px 0;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  p {
    color: #6b7280;
    font-size: 16px;
    margin: 0;
  }
`;

export const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
`;

export const SummaryCard = styled.div`
  background: ${(props) =>
    props.dark ? "linear-gradient(135deg, #111827, #000000)" : "white"};
  color: ${(props) => (props.dark ? "#ffd700" : "#111827")};
  padding: 30px;
  border-radius: 20px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;

  .icon-bg {
    position: absolute;
    right: -10px;
    bottom: -10px;
    font-size: 100px;
    opacity: 0.1;
    color: ${(props) => (props.dark ? "white" : "#000")};
  }

  span {
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 1px;
    opacity: 0.8;
    color: ${(props) => (props.dark ? "#ccc" : "#666")};
    font-weight: 600;
  }

  h2 {
    font-size: 36px;
    margin: 10px 0 0 0;
    font-weight: 900;
  }
`;

export const OptionsSection = styled.div`
  margin-bottom: 40px;

  h3 {
    margin: 0 0 20px 0;
    font-size: 20px;
    color: #374151;
    font-weight: 700;
    display: flex;
    align-items: center;
  }
`;

export const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
`;

export const OptionCard = styled.div`
  background: white;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.02);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08);
    border-color: #d4af37;
  }

  .card-content {
    padding: 25px;
  }

  .icon-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 15px;

    .title {
      font-size: 18px;
      font-weight: 800;
      color: #111827;
    }
  }

  .desc {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.5;
    margin-bottom: 20px;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      margin-bottom: 12px;
      font-size: 14px;
      color: #374151;

      svg {
        margin-top: 3px;
        flex-shrink: 0;
      }

      strong {
        color: #111827;
      }
    }
  }
`;

export const CheckButton = styled.button`
  width: 100%;
  background: #f9fafb;
  border: none;
  border-top: 1px solid #e5e7eb;
  padding: 15px;
  color: #d4af37;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.2s;
  text-transform: uppercase;

  &:hover {
    background: #fffbf0;
    color: #b88a00;
  }

  svg {
    font-size: 14px;
  }
`;

export const ActionsBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;

  .buttons {
    display: flex;
    gap: 15px;
    margin-left: auto;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;

    .buttons {
      margin-left: 0;
      flex-direction: column;
    }
  }
`;

export const TabContainer = styled.div`
  display: flex;
  gap: 10px;
  background: #e5e7eb;
  padding: 4px;
  border-radius: 12px;
  width: fit-content;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const TabButton = styled.button`
  background: ${(props) => (props.active ? "white" : "transparent")};
  color: ${(props) => (props.active ? "#D4AF37" : "#6b7280")};
  font-weight: 700;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${(props) =>
    props.active ? "0 2px 5px rgba(0,0,0,0.1)" : "none"};
  font-size: 14px;

  &:hover {
    color: ${(props) => (props.active ? "#D4AF37" : "#374151")};
  }

  @media (max-width: 768px) {
    flex: 1;
  }
`;

export const ContentWrapper = styled.div`
  animation: ${fadeIn} 0.5s ease-out;
`;

export const ActionButton = styled.button`
  background: ${(props) =>
    props.primary ? "linear-gradient(135deg, #D4AF37, #B88A00)" : "white"};
  color: ${(props) => (props.primary ? "white" : "#D4AF37")};
  border: ${(props) => (props.primary ? "none" : "2px solid #D4AF37")};
  padding: 12px 24px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: ${(props) =>
    props.primary ? "0 4px 15px rgba(212, 175, 55, 0.4)" : "none"};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(212, 175, 55, 0.3);
    background: ${(props) =>
      props.primary ? "linear-gradient(135deg, #F9D976, #D4AF37)" : "#fffbf0"};
  }

  &:disabled {
    background: #e5e7eb;
    color: #9ca3af;
    border-color: #e5e7eb;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }
`;

export const StatusBadge = styled.span`
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  padding: 4px 8px;
  border-radius: 4px;
  background-color: ${(props) => props.bg};
  color: ${(props) => props.color};
  border: 1px solid ${(props) => props.color};
  letter-spacing: 0.5px;
`;

export const InvestmentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
`;

export const InvestmentCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 25px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;
  position: relative;
  border-left: 5px solid
    ${(props) => (props.type === "monthly" ? "#10b981" : "#D4AF37")};

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }

  .header {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 15px;
    padding-bottom: 15px;
    border-bottom: 1px solid #f3f4f6;

    .top-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
    }

    .id {
      font-size: 14px;
      color: #9ca3af;
      font-weight: 600;
    }

    .badge {
      align-self: flex-start;
      font-size: 11px;
      padding: 4px 10px;
      border-radius: 20px;
      font-weight: 700;
      text-transform: uppercase;
      background: ${(props) =>
        props.type === "monthly"
          ? "rgba(16, 185, 129, 0.1)"
          : "rgba(212, 175, 55, 0.1)"};
      color: ${(props) => (props.type === "monthly" ? "#059669" : "#b48a1e")};
    }
  }

  .balance-section {
    margin-bottom: 20px;

    label {
      font-size: 12px;
      color: #6b7280;
      font-weight: 600;
      text-transform: uppercase;
    }

    h3 {
      margin: 5px 0;
      font-size: 24px;
      font-weight: 800;
      color: #111827;
    }
  }

  .info-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
    font-size: 14px;

    span {
      color: #6b7280;
    }

    strong {
      color: #374151;
      font-weight: 700;
    }

    .highlight {
      color: ${(props) => (props.type === "monthly" ? "#059669" : "#9ca3af")};
    }
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(5px);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

export const ModalContent = styled.div`
  background: white;
  width: 100%;
  max-width: 600px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
  animation: ${fadeIn} 0.3s ease-out;
`;

export const ModalHeader = styled.div`
  background: #111827;
  padding: 20px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    color: #ffd700;
    margin: 0;
    font-size: 18px;
    font-weight: 700;
  }

  button {
    background: transparent;
    border: none;
    color: white;
    font-size: 20px;
    cursor: pointer;
    opacity: 0.7;
    &:hover {
      opacity: 1;
    }
  }
`;

export const ModalBody = styled.div`
  padding: 30px;
  max-height: 70vh;
  overflow-y: auto;

  .search-box {
    position: relative;
    margin-bottom: 20px;

    input {
      width: 100%;
      padding: 12px 15px 12px 45px;
      border: 2px solid #e5e7eb;
      border-radius: 10px;
      font-size: 16px;
      outline: none;
      transition: border-color 0.3s;

      &:focus {
        border-color: #d4af37;
      }
    }

    svg {
      position: absolute;
      left: 15px;
      top: 50%;
      transform: translateY(-50%);
      color: #9ca3af;
    }
  }
`;

export const EligibilityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

export const EligibilityItem = styled.div`
  background: #fff;
  border: 1px solid #10b981;
  border-left: 5px solid #10b981;
  border-radius: 8px;
  padding: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);

  .left-info {
    .contract-id {
      font-weight: 700;
      color: #111827;
      font-size: 15px;
    }
    .months {
      font-size: 12px;
      color: #6b7280;
      margin-top: 3px;
    }
  }

  .right-info {
    text-align: right;
    .value {
      font-weight: 700;
      color: #111827;
      font-size: 16px;
    }
    .status {
      font-size: 12px;
      color: #10b981;
      font-weight: 600;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 5px;
      margin-top: 3px;
    }
  }
`;

export const ModalFooter = styled.div`
  padding: 20px 30px;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 15px;

  select {
    width: 100%;
    padding: 12px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    background: white;
    font-size: 14px;
  }
`;

export const ContractOption = styled.div`
  border: 2px solid ${(props) => (props.selected ? "#D4AF37" : "#f3f4f6")};
  background: ${(props) => (props.selected ? "#fffbf0" : "white")};
  padding: 15px;
  border-radius: 12px;
  margin-bottom: 10px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s;

  &:hover {
    border-color: ${(props) => (props.selected ? "#D4AF37" : "#d1d5db")};
  }

  .info {
    h4 {
      margin: 0 0 5px 0;
      color: #111827;
    }
    p {
      margin: 0;
      font-size: 12px;
      color: #6b7280;
    }
  }

  .value {
    font-weight: 700;
    color: #111827;
  }
`;
